import { round } from 'lodash';
import isNumEqualIsh from './tools/isNumEqualIsh';

const float = (num, fixedDigits = 2) => {
	return parseFloat(num).toFixed(fixedDigits);
};

/**
 *
 * @param {number} num
 * @param {object} options
 * @param {boolean} options.dollarSign adds $ prefix
 * @param {number} options.fixedDigits number of places after the decimal point
 * @param {boolean} options.commas adds commas every 3 digits before decimal point
 * @param {boolean} options.raw returns original number along with formatted
 * @param {boolean} options.sign adds + prefix
 * @param {string} options.timeUnit adds '/ timeUnit' suffix, can also pass true to use default '/ mo'
 * @returns {string} of formatted number
 * @returns {object} if raw=true with raw and formatted number
 */

const currency = (
	num,
	{
		dollarSign = true,
		fixedDigits = 2,
		commas = true,
		raw = false,
		sign = false,
		timeUnit = false,
	} = {},
) => {
	let formattedNum;

	// Return the passed in values if invalid.
	if (Number.isNaN(num) || num === '' || num === null || num === undefined) {
		return '';
	}

	formattedNum = float(num, fixedDigits);
	// TODO Check for NaN
	const copy = formattedNum;
	if (commas) {
		formattedNum = (formattedNum = String(formattedNum)).replace(
			/\B(?=(\d{3})+(?!\d))/g,
			',',
		);
	}
	if (dollarSign) {
		formattedNum = (formattedNum = String(formattedNum)).replace(
			/(^-?)/,
			'$1$',
		);
	}
	if (sign && !/^-/.test(formattedNum)) {
		formattedNum = `+${formattedNum}`;
	}
	if (timeUnit) {
		if (timeUnit === true) {
			formattedNum = `${formattedNum} / mo`;
		} else {
			formattedNum = `${formattedNum} / ${timeUnit}`;
		}
	}
	if (raw) {
		return { display: formattedNum, raw: copy };
	}

	return formattedNum;
};

const bytes = (inSize) => {
	const size = typeof inBytes === 'string' ? parseFloat(inSize) : inSize;
	return size >= 1000 ? `${size / 1000}TB` : `${size}GB`;
};

// not to be confused with giga bytes.
const toGibibyte = (megabitsArg) => {
	const megabits = Number(megabitsArg);
	return megabits < 1024 ? `${megabits}MB` : `${round(megabits / 1024, 1)}GB`; // TODO: label 'GiB'?
};

const perMonth = (aNumber, options) => {
	return currency(aNumber, { ...options, timeUnit: 'mo' });
};

const perYear = (aNumber) => {
	return currency(aNumber, { timeUnit: 'yr' });
};

// This regex should not be relied upon as a secure way of sanitizing input.
// For more information: https://stackoverflow.com/questions/5002111/how-to-strip-html-tags-from-string-in-javascript
const stripHTML = (text = '') => text.replace(/<\/?[^>]+(>|$)/g, '');

const toInt = (number) =>
	Math.round(
		typeof number === 'number'
			? number
			: Number(number?.replace(/[a-zA-Z]/g, '') || 0),
	);

const toNumberOrEmptyString = (number) => {
	if (typeof number === 'number' || number === '') return number;
	const converted = Number(number);
	if (Number.isNaN(converted)) return '';
	return converted;
};

const formatZone = (item) => {
	return `${item?.name} - ${item?.region?.name}`;
};

const zone = (items, zoneId) => {
	const data = items.find((item) => isNumEqualIsh(item.id, zoneId));
	return formatZone(data);
};

export {
	currency,
	bytes,
	toGibibyte,
	float,
	perMonth,
	perYear,
	stripHTML,
	toInt,
	toNumberOrEmptyString,
	zone,
	formatZone,
};
